import { spLog } from "@eventr/common";

/**
 * Lazily-loaded translation bundles.
 *
 * These used to be 44 static `import`s, which put every locale (~2.2 MB minified,
 * ~0.65 MB gzipped) into the main chunk even though only one is ever in use. Since
 * the app ships as a Capacitor bundle — and is re-downloaded in full on every
 * @capgo OTA update — that was dead weight in the binary, on the wire, and in
 * startup parse time.
 *
 * `import.meta.glob` (lazy by default) makes Vite emit one hashed chunk per locale,
 * fetched from local disk on demand. No network, no i18next-http-backend.
 */
const loaders = import.meta.glob<{ default: Record<string, unknown> }>([
  "./messages/*.json",
  // ar/he/ku are translated and kept up to date, but were never registered with
  // i18next (the UI has no RTL support) so nothing can request them. Excluding them
  // keeps ~170 KB of unreachable chunks out of the app binary. Drop the exclusion
  // and add them to FILE_BY_LANGUAGE/availableLang together when they go live.
  "!./messages/{ar,he,ku}.json",
]);

/**
 * i18next language tag -> filename in ./messages.
 *
 * Only tags listed here (or whose base language resolves below) are supported;
 * anything else falls back to `en-GB`. Note that `ar`, `he` and `ku` exist on disk
 * but are deliberately unmapped — they were never registered before this change
 * either, as the UI has no RTL support.
 */
const FILE_BY_LANGUAGE: Record<string, string> = {
  // Regional variants that need their own file rather than the base language's.
  "en-US": "en_US",
  "pt-BR": "pt_BR",

  // Base languages whose filename differs from the tag.
  fil: "tl",
  nb: "no", // browsers commonly report Norwegian as nb-NO / nn-NO
  nn: "no",
};

/**
 * Chinese is split by script, not region, so it can't go through the table above:
 * the tag may carry the script directly (`zh-Hant`, and Safari reports `zh-Hant-TW`
 * on a Traditional device) or only imply it via region (`zh-TW`, `zh-HK`, `zh-MO`).
 * Simplified is the default, matching `zh` and `zh-CN`.
 */
const TRADITIONAL_CHINESE = new Set(["Hant", "TW", "HK", "MO"]);

/**
 * Resolves an i18next language tag to a bundle filename.
 *
 * Tries the full tag first (so `en-US` and `pt-BR` keep their own files), then the
 * base language (so `de-DE`, `es-ES` and unlisted variants like `en-AU` or `fr-CA`
 * all land on the right file).
 */
const fileFor = (language: string): string | undefined => {
  const [base = language, ...subtags] = language.split("-");

  const candidate =
    FILE_BY_LANGUAGE[language] ??
    (base === "zh" ? (subtags.some((tag) => TRADITIONAL_CHINESE.has(tag)) ? "zh_Hant" : "zh_Hans") : undefined) ??
    FILE_BY_LANGUAGE[base] ??
    base;

  return `./messages/${candidate}.json` in loaders ? candidate : undefined;
};

/**
 * Loads a single locale's translations, or returns undefined if the language has no
 * bundle. Returning undefined lets i18next fall through to `fallbackLng`.
 */
export const loadLocale = async (language: string): Promise<Record<string, unknown> | undefined> => {
  spLog(`loadLocale called for language: "${language}"`, ["i18n"]);

  const file = fileFor(language);
  spLog(`loadLocale resolved file: ${file ?? "undefined"}`, ["i18n"]);

  const loader = file ? loaders[`./messages/${file}.json`] : undefined;
  if (!loader) {
    spLog(`loadLocale no loader found for "${language}" (file: ${file}), will fall back to fallbackLng`, ["i18n"]);
    return undefined;
  }

  const module = await loader();
  spLog(`loadLocale successfully loaded locale: "${language}" -> "${file}.json"`, ["i18n"]);

  return module.default;
};
